import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { LoginGuard } from './guards/login.guard';
import { ownTransferGuard } from './guards/own-transfer.guard';
import { canMakeTransfersGuard } from './guards/can-make-transfers.guard';
import { TwoFactorGuard } from './guards/two-factor.guard';
import { MaintenanceGuard } from './guards/maintenance.guard';
import { internalTransfersEnabledGuard } from './guards/internal-transfers-enabled.guard';
import { accountNotSuspendedGuard } from './guards/account-not-suspended.guard';
import { customerActiveGuard } from './guards/customer-active.guard';
import { serviceAccessGuard } from './guards/service-access.guard';
import { completeProfileGuard } from './guards/complete-profile.guard';
import { kycApprovedForExtractionGuard } from './guards/kyc-approved-for-extraction.guard';
import { kycApprovedForAuctionGuard } from './guards/kyc-approved-for-auction.guard';
import { referralGuard } from './guards/referral.guard';
import Component404 from './pages/404/404.component';
import ComponentError from './pages/error/error.component';
import ComponentMaintenance from './pages/maintenance/maintenance.component';
import { DashboardLayoutComponent } from './theme/layouts/dashboard/dashboard.component';
import { GuestComponent } from './theme/layouts/guest/guest.component';
import { HomeLayoutComponent } from './theme/layouts/home/home-layout.component';

const common: Partial<Route> = {
  pathMatch: 'full',
  canActivate: [AuthGuard]
};

const routes: Routes = [
  {
    path: 'crypto-contact',
    redirectTo: '/fund-account/crypto?openChat=true',
    pathMatch: 'full'
  },
  {
    path: 'pay/:id',
    redirectTo: '/',
    pathMatch: 'full'
  },
  {
    path: 'payto/:id',
    redirectTo: '/',
    pathMatch: 'full'
  },
  {
    // Landing PÚBLICA del enlace de cobro: ofrece pagar con transferencia interna
    // (requiere login → /transfer) o con crypto (USDT, pagador anónimo). Antes esta
    // ruta redirigía directo a /transfer; ahora deja elegir el método de pago.
    path: 'paylink/:id',
    component: GuestComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        data: { requiresAuth: false },
        loadComponent: () =>
          import('./pages/paylink-pay/paylink-pay.component').then((c) => c.PaylinkPayComponent)
      }
    ]
  },
  {
    path: 'login',
    component: HomeLayoutComponent,
    canActivate: [MaintenanceGuard, LoginGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/v2/login/login-home.component').then((c) => c.LoginHomePageComponent),
        data: { requiresAuth: false },
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'signup',
    component: HomeLayoutComponent,
    canActivate: [MaintenanceGuard, LoginGuard, referralGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/v2/signup/register-home.component').then(
            (c) => c.RegisterHomePageComponent
          ),
        data: { requiresAuth: false },
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'forgot-password',
    component: HomeLayoutComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/forgot-password/forgot-password.component').then(
            (c) => c.ForgotPasswordPageComponent
          ),
        data: { requiresAuth: false },
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'change-password',
    component: HomeLayoutComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        canActivate: [AuthGuard],
        path: '',
        loadComponent: () =>
          import('./pages/forgot-password/change-password.component').then(
            (c) => c.ChangePasswordPageComponent
          ),
        data: { requiresAuth: true },
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'reset-password',
    component: HomeLayoutComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/forgot-password/reset-password.component').then(
            (c) => c.ResetPasswordPageComponent
          ),
        data: { requiresAuth: false },
        pathMatch: 'full'
      }
    ]
  },
  {
    path: '',
    component: DashboardLayoutComponent,
    canActivate: [MaintenanceGuard, AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((c) => c.DashboardComponent),
        data: { requiresAuth: true, bigHeader: true },
        ...common
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./pages/transactions/transactions.component').then(
            (c) => c.TransactionsComponent
          ),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'beneficiaries',
        loadComponent: () =>
          import('./pages/contacts/beneficiaries.component').then((c) => c.BeneficiariesComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'market',
        loadComponent: () =>
          import('./pages/market/market.component').then((c) => c.MarketComponent),
        canActivate: [AuthGuard],
        children: [
          { path: '', redirectTo: 'stores', pathMatch: 'full' },
          {
            path: 'stores',
            loadComponent: () =>
              import('./pages/market/market-stores.component').then((c) => c.MarketStoresComponent),
            data: { requiresAuth: true, containerClass: 'sm:max-w-3xl sm:pt-16' },
            pathMatch: 'full',
            canActivate: [AuthGuard]
          },
          {
            path: 'recargas',
            loadComponent: () =>
              import('./pages/market/market-recharges.component').then(
                (c) => c.MarketRechargesComponent
              ),
            data: { requiresAuth: true, containerClass: 'sm:max-w-3xl sm:pt-16' },
            pathMatch: 'full',
            canActivate: [AuthGuard]
          },
          {
            // Detalle de una recarga por referencia: al recargar la página o entrar
            // directo a esta URL se muestra el último step (estado) de esa recarga.
            path: 'recargas/:reference',
            loadComponent: () =>
              import('./pages/market/market-recharges.component').then(
                (c) => c.MarketRechargesComponent
              ),
            data: { requiresAuth: true, containerClass: 'sm:max-w-3xl sm:pt-16' },
            pathMatch: 'full',
            canActivate: [AuthGuard]
          }
        ]
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./pages/payments/payments.component').then((c) => c.PaymentsComponent),
        data: { requiresAuth: true },
				canActivate: [AuthGuard, TwoFactorGuard, serviceAccessGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        path: 'paylinks',
        loadComponent: () =>
          import('./pages/paylink/payment-link-list.component').then(
            (c) => c.PaymentLinkListComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        ...common
      },
      {
        path: 'paylinks/new',
        loadComponent: () =>
          import('./pages/paylink/new-payment-link.component').then(
            (c) => c.NewPaymentLinkComponent
					),
				canActivate: [AuthGuard, completeProfileGuard],

        data: { requiresAuth: true },
        ...common
      },
      // {
      //   path: 'debts',
      //   loadComponent: () =>
      //     import('./pages/debts/debts-list.component').then((c) => c.DebtsListComponent),
      //   data: { requiresAuth: true },
      //   ...common
      // },
      // {
      //   path: 'cards',
      //   loadComponent: () => import('./pages/cards/cards.component').then((c) => c.CardsComponent),
      //   data: { requiresAuth: true, containerClass: 'max-w-2xl' },
      //   ...common
      // },
      {
        path: 'help-center',
        loadComponent: () =>
          import('./pages/help-center/help-center.component').then((c) => c.HelpCenterComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications.component').then((c) => c.NotificationsComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'app-updates/updates',
        loadComponent: () =>
          import('./pages/app-updates/updates.component').then((c) => c.UpdatesComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'app-updates/announcements',
        loadComponent: () =>
          import('./pages/app-updates/announcements.component').then((c) => c.AnnouncementsComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'app-updates/updates/:id',
        loadComponent: () =>
          import('./pages/app-updates/app-update-detail.component').then((c) => c.AppUpdateDetailComponent),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'auctions',
        loadComponent: () =>
          import('./pages/auctions/auctions-list.component').then((c) => c.AuctionsListComponent),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        path: 'statistics',
        loadComponent: () =>
          import('./pages/merchant-statistics/merchant-statistics.component').then(
            (c) => c.MerchantStatisticsComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard],
        pathMatch: 'full'
      },
      {
        path: 'collections',
        loadComponent: () =>
          import('./pages/merchant-collections/merchant-collections.component').then(
            (c) => c.MerchantCollectionsComponent
          ),
        // Contenedor ancho (como /settings): la tabla de cobros y las tarjetas de
        // resumen no caben en el `max-w-xl` que aplica el layout por defecto.
        data: { requiresAuth: true, containerClass: 'lg:max-w-5xl max-w-4xl sm:pt-16' },
        canActivate: [AuthGuard],
        pathMatch: 'full'
      },
      {
        path: 'auctions/:id',
        loadComponent: () =>
          import('./pages/auctions/auction-detail.component').then((c) => c.AuctionDetailComponent),
        data: { requiresAuth: true, containerClass: 'lg:max-w-5xl max-w-4xl sm:pt-16' },
        canActivate: [AuthGuard, completeProfileGuard, kycApprovedForAuctionGuard],
        pathMatch: 'full'
      },
      {
        path: 'integration-api',
        loadComponent: () =>
          import('./pages/integration-api/integration-api.component').then(
            (c) => c.IntegrationApiComponent
          ),
				data: {
					containerClass: 'lg:max-w-5xl max-w-4xl'
				},
        ...common
      },
      {
        path: 'integration-api/request-credentials',
        loadComponent: () =>
          import('./pages/integration-api/request-credentials/request-credentials.component').then(
            (c) => c.RequestCredentialsComponent
          ),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'docs/developers/merchant-integration',
        loadComponent: () =>
          import('./pages/docs/merchant-integration/merchant-integration.component').then(
            (c) => c.MerchantIntegrationDocComponent
          ),
        data: { requiresAuth: true, containerClass: 'lg:max-w-6xl max-w-4xl' },
        ...common
      },
      {
        path: 'qr/share',
        loadComponent: () =>
          import('./pages/shareQr/shareQr.component').then((c) => c.ShareQrComponent),
        data: {
          requiresAuth: true,
          pageLayoutClass: '!pb-14 sm:!pb-0'
        },
        ...common
      },
      {
        path: 'send',
        loadComponent: () => import('./pages/send/send.component').then((c) => c.SendComponent),
				data: { requiresAuth: true },
				canActivate: [AuthGuard, completeProfileGuard],
        ...common
      },
      {
        path: 'deposit',
        loadComponent: () =>
          import('./pages/deposit/deposit.component').then((c) => c.DepositComponent),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        ...common
      },
      {
        path: 'fund-account',
        loadComponent: () =>
          import('./pages/deposit/fund-account/fund-account.component').then(
            (c) => c.FundAccountComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        path: 'fund-account/card-recharge',
        loadComponent: () =>
          import('./pages/deposit/fund-account/card-recharge/card-recharge.component').then(
            (c) => c.CardRechargeComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        path: 'fund-account/crypto',
        loadComponent: () =>
          import('./pages/deposit/fund-account/crypto-contact/crypto-contact.component').then(
            (c) => c.CryptoContactComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        // Mismo componente, con el uuid del pago en la URL: permite recargar la
        // página y recuperar el estado real del pago (pagado/expirado/cancelado).
        path: 'fund-account/crypto/:paymentId',
        loadComponent: () =>
          import('./pages/deposit/fund-account/crypto-contact/crypto-contact.component').then(
            (c) => c.CryptoContactComponent
          ),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, completeProfileGuard],
        pathMatch: 'full'
      },
      {
        path: 'fund-account/payment-result',
        loadComponent: () =>
          import('./pages/deposit/fund-account/payment-result/payment-result.component').then(
            (c) => c.PaymentResultComponent
          ),
        data: { requiresAuth: true },
        ...common
      },
      {
        path: 'qr/generate',
        loadComponent: () =>
          import('./pages/deposit/generate-qr/generate-qr.component').then(
            (c) => c.GenerateQrComponent
          ),
        data: { requiresAuth: true },
        ...common
      },
			{
        path: 'transfer',
        loadComponent: () =>
          import('./pages/transfer-v2/transfer.component').then((c) => c.TransferComponent),
        data: { requiresAuth: true },
				canActivate: [AuthGuard, TwoFactorGuard, completeProfileGuard, customerActiveGuard, accountNotSuspendedGuard, canMakeTransfersGuard, internalTransfersEnabledGuard, kycApprovedForExtractionGuard],
        pathMatch: 'full'
      },
      {
        path: 'own-transfer',
        loadComponent: () =>
          import('./pages/own-transfer/own-transfer.component').then((c) => c.OwnTransferComponent),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, TwoFactorGuard, ownTransferGuard, customerActiveGuard, accountNotSuspendedGuard, canMakeTransfersGuard, internalTransfersEnabledGuard],
        pathMatch: 'full'
      },
      {
        path: 'service-payment',
        loadComponent: () =>
          import('./pages/service-payment/service-payment-list.component').then((c) => c.ServicePaymentListComponent),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, TwoFactorGuard, completeProfileGuard, customerActiveGuard, accountNotSuspendedGuard, canMakeTransfersGuard, kycApprovedForExtractionGuard],
        pathMatch: 'full'
      },
      {
        path: 'service-payment/:key',
        loadComponent: () =>
          import('./pages/service-payment/service-payment-form.component').then((c) => c.ServicePaymentFormComponent),
        data: { requiresAuth: true },
        canActivate: [AuthGuard, TwoFactorGuard, completeProfileGuard, customerActiveGuard, accountNotSuspendedGuard, canMakeTransfersGuard, kycApprovedForExtractionGuard],
        pathMatch: 'full'
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then((c) => c.SettingsComponent),
        data: {
          requiresAuth: true,
          containerClass: 'lg:max-w-5xl max-w-4xl'
        },
        children: [
          {
            path: 'profile',
            loadComponent: () =>
              import('./pages/settings/profile/profile.component').then(
                (c) => c.SettingsProfileComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Mi Perfil',
              pageLayoutClass: 'pb-14 sm:!pb-0'
            }
          },
          {
            path: 'preferences',
            loadComponent: () =>
              import('./pages/settings/preferences/preferences.component').then(
                (c) => c.SettingsPreferencesComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Mis Preferencias'
            }
          },

          {
            path: 'accounts',
            loadComponent: () =>
              import('./pages/settings/accounts/accounts.component').then(
                (c) => c.SettingsAccountsComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Mis Cuentas'
            }
          },
          {
            path: 'security',
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Seguridad'
            },
            loadComponent: () =>
              import('./pages/settings/security/security.component').then(
                (c) => c.SettingsSecurityComponent
              ),
            ...common
          },
          {
            path: 'kyc',
            loadComponent: () =>
              import('./pages/settings/kyc/kyc.component').then(
                (c) => c.SettingsKycComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'KYC'
            }
          },
          {
            path: 'company-kyc',
            loadComponent: () =>
              import('./pages/settings/company-kyc/company-kyc.component').then(
                (c) => c.CompanyKycComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'KYC de Empresa'
            }
          },
          {
            path: 'referrals',
            loadComponent: () =>
              import('./pages/settings/referrals/referrals.component').then(
                (c) => c.SettingsReferralsComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Mis enlaces de invitación'
            }
          },
          {
            path: 'security/change-password',
            loadComponent: () =>
              import('./pages/settings/security/change-password.component').then(
                (c) => c.ChangePasswordComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Cambiar Contraseña'
            }
          },
          {
            path: 'security/two-factor',
            loadComponent: () =>
              import('./pages/settings/security/two-factor.component').then(
                (c) => c.TwoFactorComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Verificación en dos pasos'
            }
          },
          {
            path: 'security/biometric',
            loadComponent: () =>
              import('./pages/settings/security/biometric.component').then(
                (c) => c.SettingsBiometricComponent
              ),
            ...common,
            data: {
              requiresAuth: true,
              containerClass: 'lg:max-w-5xl max-w-4xl',
              title: 'Inicio de sesión biométrico'
            }
          }
        ]
      }
    ]
  },
  {
    // Flujo de saldo existente (requiere login) del cobro a comercio. Se entra desde
    // la landing pública. Va ANTES de payment/:id y con `home` en un hijo de path
    // vacío para que herede el :id del padre (estrategia emptyOnly).
    path: 'payment/:id/balance',
    component: GuestComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/home/home.component')
      }
    ]
  },
  {
    // Landing PÚBLICA del cobro a comercio: ofrece pagar con saldo SuperPay (login),
    // crypto (USDT, pagador anónimo) o tarjeta (próximamente).
    path: 'payment/:id',
    component: GuestComponent,
		canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        data: { requiresAuth: false },
        loadComponent: () =>
          import('./pages/merchant-pay/merchant-pay.component').then((c) => c.MerchantPayComponent)
      }
    ]
  },
  {
    path: 'payment-login',
    component: GuestComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./pages/login/login.component')
      }
    ]
  },

  {
    path: '404',
    component: Component404,
    canActivate: [MaintenanceGuard]
	},
	{
    path: 'maintenance',
    component: ComponentMaintenance,
    canActivate: [MaintenanceGuard]
  },
  {
    path: 'error/:error_code',
    component: ComponentError,
    canActivate: [MaintenanceGuard]
  },
  { path: '**', redirectTo: '/404' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableViewTransitions: true })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
